import {
  useIsChatObjectRefsEnabled,
  useIsTaskEnableAssignmentEnabled,
} from '@/features/flags';
import type { SubmitProjectTurnArgs } from '@heidi/agents';
import {
  bindWorkTaskLaunches,
  buildTaskLaunchPromptPreamble,
  existingWorkProjectIdForVerbatimLaunch,
  type WorkHomeTaskLaunch,
} from '@heidi/agents/data';
import { GET_CLINICAL_TASKS } from '@heidi/tasks/contracts';
import {
  assignClinicalTask,
  linkClinicalTask,
  markClinicalTaskInProgress,
} from '@heidi/tasks/data';
import { useQueryUser } from '@heidi/workspace/data';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

/**
 * Next Steps tasks riding a chat's opening turn: the agent preamble on the way
 * out, then the same link/assign/start writes Work home makes once the chat exists.
 */
// eslint-disable-next-line @heidi/rules/no-new-carved-domain-data -- noun collision: the object workspace is an agents surface, not workspace data; this composes @heidi/tasks/data and @heidi/agents/data door calls with app flags
export function useObjectWorkspaceTaskLaunches() {
  const queryClient = useQueryClient();
  const isTaskAssignmentEnabled = useIsTaskEnableAssignmentEnabled();
  const chatObjectRefsEnabled = useIsChatObjectRefsEnabled();
  const { data: currentUserId } = useQueryUser((data) => data?.kinde_user_id ?? null);
  const pendingRef = useRef<readonly WorkHomeTaskLaunch[]>([]);

  /** The Work chat that already owns these tasks, while the composer still reads their titles. */
  const existingProjectForLaunches = useCallback(
    (launches: readonly WorkHomeTaskLaunch[], args: SubmitProjectTurnArgs) =>
      args.files?.length || args.attachment_ids?.length
        ? undefined
        : existingWorkProjectIdForVerbatimLaunch(launches, args.displayText ?? args.text),
    []
  );

  const withLaunchPreamble = useCallback(
    (launches: readonly WorkHomeTaskLaunch[], args: SubmitProjectTurnArgs) => ({
      ...args,
      text: `${buildTaskLaunchPromptPreamble(launches, { refs: chatObjectRefsEnabled })}${args.text}`,
      displayText: args.displayText ?? args.text,
    }),
    [chatObjectRefsEnabled]
  );

  const holdLaunches = useCallback((launches: readonly WorkHomeTaskLaunch[]) => {
    pendingRef.current = launches;
  }, []);

  const dropLaunches = useCallback(() => {
    pendingRef.current = [];
  }, []);

  const bindHeldLaunches = useCallback(
    (projectId: string) => {
      const launches = pendingRef.current;
      pendingRef.current = [];
      if (launches.length === 0) return;
      void bindWorkTaskLaunches(projectId, launches, {
        markTaskInProgress: markClinicalTaskInProgress,
        linkTaskToWorkProject: (taskId, workProjectId) =>
          linkClinicalTask(taskId, { type: 'WORK_PROJECT', id: workProjectId }),
        claimTaskOwner: (launch) =>
          currentUserId && isTaskAssignmentEnabled && !launch.assigneeId
            ? assignClinicalTask(launch.taskId, currentUserId)
            : Promise.resolve(),
        invalidateClinicalTasks: () =>
          queryClient.invalidateQueries({ queryKey: [GET_CLINICAL_TASKS] }),
      });
    },
    [currentUserId, isTaskAssignmentEnabled, queryClient]
  );

  return {
    existingProjectForLaunches,
    withLaunchPreamble,
    holdLaunches,
    dropLaunches,
    bindHeldLaunches,
  };
}
